import React, { useCallback, FunctionComponent, useMemo } from 'react';
import {
  TreeView,
  TreeItem,
  TTreeViewProps,
  Button,
  OpenInNewIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
} from '@rbx/ui';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { HttpMethods } from '@modules/openCloud/utils/oasTypes';
import { isInteractKey } from '@modules/layout/layout/utils/keyboardNavigationHandler';
import useAccessibilityStyles from '@modules/miscellaneous/common/styles/accessibilityStyles';
import {
  REGEX_INHERIT_INSTANCE,
  REGEX_INHERIT_OBJECT,
} from '@modules/apiReference/page/constants/apiReferenceConstants';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import { useLocalization } from '@rbx/intl';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import { REFERENCE_ENGINE_CLASSES_NODE_PATH } from '../../../apiReference/page/utils/sidebar';
import EndpointTreeItem from './EndpointTreeItem';
import useTreeStyles from './Tree.styles';
import TreeNode from '../interfaces/TreeNode';
import useSideBarContent from '../hooks/useSideBarContent';

export function getTreeItemElementId(nodeId: string) {
  return `tree-${nodeId}`;
}
export type SingleSelectTreeViewState = {
  expanded: string[];
  selected: string;
};

export type TreeProps = SingleSelectTreeViewState & {
  setExpanded: (nodeIds: string[]) => void;
  setSelected: (nodeIds: string) => void;
  // By default, click label text will trigger expand & select,
  // if set to true only icon will trigger expand
  tree: TreeNode;
  className?: string;
  isLeftNav?: boolean;
} & Pick<TTreeViewProps, 'defaultCollapseIcon' | 'defaultExpandIcon'>;

enum ECustomTreeComponentName {
  OpenCloudEndpoint = 'OpenCloudEndpoint',
}

const TreeLabel: FunctionComponent<{
  node: TreeNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
}> = ({ node, onClick, onKeyDown }) => {
  const { enableNewLayout } = useAppConfig();
  const {
    classes: { link, openInNewIcon },
    cx,
  } = useTreeStyles({ enableNewLayout: enableNewLayout ?? false });
  const {
    classes: { noStylesBtn },
  } = useAccessibilityStyles();

  const isHttpLink = node.path?.startsWith('http');
  const showOpenInNewIcon = isHttpLink && !node.path?.startsWith('https://create.roblox.com/docs');

  if (node.path) {
    return (
      <DynamicallyRouted.Link
        href={node.path}
        underline='none'
        color='inherit'
        role='menuitem'
        className={link}
        onClick={onClick}>
        {node.component === ECustomTreeComponentName.OpenCloudEndpoint ? (
          <EndpointTreeItem
            method={node?.componentProps?.method as HttpMethods}
            title={node.value}
          />
        ) : (
          <React.Fragment>
            {node.props?.label ?? node.value}
            {showOpenInNewIcon && <OpenInNewIcon className={openInNewIcon} />}
          </React.Fragment>
        )}
      </DynamicallyRouted.Link>
    );
  }

  return (
    <Button
      role='menuitem'
      size='small'
      onKeyDown={onKeyDown}
      className={cx(noStylesBtn, link)}
      onClick={onClick}>
      {node.props?.label ?? node.value}
    </Button>
  );
};

const BaseTreeItem: FunctionComponent<{
  node: TreeNode;
  classes?: Record<string, string>;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  children?: React.ReactNode;
}> = ({ node, classes, onClick, onKeyDown, children }) => (
  <TreeItem
    tabIndex={-1}
    id={getTreeItemElementId(node.id)}
    key={node.id}
    classes={classes}
    nodeId={node.id}
    label={<TreeLabel node={node} onClick={onClick} onKeyDown={onKeyDown} />}>
    {children}
  </TreeItem>
);

const ClassInheritanceList = React.memo<{
  treeNode: TreeNode;
  engineClassInheritanceList: string;
  cx: (classes: string, conditionalClasses?: Record<string, boolean>) => string;
  defaultLabelStyle: string;
  selectedClassInheritanceListItem: string;
  selected: string;
  handleClickTreeNode: (node: TreeNode) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleKeyboardNavigation: (node: TreeNode) => (e: React.KeyboardEvent<HTMLElement>) => void;
}>(function ClassInheritanceList({
  treeNode,
  engineClassInheritanceList,
  cx,
  defaultLabelStyle,
  selectedClassInheritanceListItem,
  selected,
  handleClickTreeNode,
  handleKeyboardNavigation,
}) {
  return (
    <ul className={engineClassInheritanceList} key={treeNode.id}>
      <li>
        <BaseTreeItem
          node={treeNode}
          classes={{
            content: cx(defaultLabelStyle, {
              [selectedClassInheritanceListItem]: selected === treeNode.id,
            }),
          }}
          onClick={handleClickTreeNode(treeNode)}
          onKeyDown={handleKeyboardNavigation(treeNode)}
        />
      </li>
      {Array.isArray(treeNode.children)
        ? treeNode.children.map((child) => (
            <ClassInheritanceList
              key={child.id}
              treeNode={child}
              engineClassInheritanceList={engineClassInheritanceList}
              cx={cx}
              defaultLabelStyle={defaultLabelStyle}
              selectedClassInheritanceListItem={selectedClassInheritanceListItem}
              selected={selected}
              handleClickTreeNode={handleClickTreeNode}
              handleKeyboardNavigation={handleKeyboardNavigation}
            />
          ))
        : null}
    </ul>
  );
});

const OpenCloudEndpointTree = React.memo<{
  node: TreeNode;
  defaultLabelStyle: string;
  openCloudEndpoint: string;
  cx: (classes: string, conditionalClasses?: Record<string, boolean>) => string;
  handleClickTreeNode: (node: TreeNode) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleKeyboardNavigation: (node: TreeNode) => (e: React.KeyboardEvent<HTMLElement>) => void;
}>(function OpenCloudEndpointTree({
  node,
  defaultLabelStyle,
  openCloudEndpoint,
  cx,
  handleClickTreeNode,
  handleKeyboardNavigation,
}) {
  if (node.component !== ECustomTreeComponentName.OpenCloudEndpoint) {
    throw Error(
      `OpenCloudEndpointTree should not be called for path: ${node.path} value: ${node.value} componentName: ${node.component}`,
    );
  }

  return (
    <BaseTreeItem
      node={node}
      classes={{ content: cx(defaultLabelStyle, { [openCloudEndpoint]: true }) }}
      onClick={handleClickTreeNode(node)}
      onKeyDown={handleKeyboardNavigation(node)}>
      {Array.isArray(node.children)
        ? node.children.map((n) => (
            <OpenCloudEndpointTree
              key={n.id}
              node={n}
              defaultLabelStyle={defaultLabelStyle}
              openCloudEndpoint={openCloudEndpoint}
              cx={cx}
              handleClickTreeNode={handleClickTreeNode}
              handleKeyboardNavigation={handleKeyboardNavigation}
            />
          ))
        : null}
    </BaseTreeItem>
  );
});

const EngineReferenceClassInheritanceTree = React.memo<{
  node: TreeNode;
  defaultLabelStyle: string;
  engineClassInheritanceTreeItem: string;
  engineClassInheritanceListContainer: string;
  engineClassInheritanceList: string;
  selectedClassInheritanceListItem: string;
  selected: string;
  cx: (classes: string, conditionalClasses?: Record<string, boolean>) => string;
  handleClickTreeNode: (node: TreeNode) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleKeyboardNavigation: (node: TreeNode) => (e: React.KeyboardEvent<HTMLElement>) => void;
}>(function EngineReferenceClassInheritanceTree({
  node,
  defaultLabelStyle,
  engineClassInheritanceTreeItem,
  engineClassInheritanceListContainer,
  engineClassInheritanceList,
  selectedClassInheritanceListItem,
  selected,
  cx,
  handleClickTreeNode,
  handleKeyboardNavigation,
}) {
  if (node.path !== REFERENCE_ENGINE_CLASSES_NODE_PATH) {
    throw Error(
      `EngineReferenceClassInheritanceTree should not be called for path: ${node.path} value: ${node.value}`,
    );
  }

  return (
    <BaseTreeItem
      node={node}
      classes={{ content: cx(defaultLabelStyle, { [engineClassInheritanceTreeItem]: true }) }}
      onClick={handleClickTreeNode(node)}
      onKeyDown={handleKeyboardNavigation(node)}>
      <div className={engineClassInheritanceListContainer}>
        {Array.isArray(node.children)
          ? node.children.map((child) => (
              <ClassInheritanceList
                key={child.id}
                treeNode={child}
                engineClassInheritanceList={engineClassInheritanceList}
                cx={cx}
                defaultLabelStyle={defaultLabelStyle}
                selectedClassInheritanceListItem={selectedClassInheritanceListItem}
                selected={selected}
                handleClickTreeNode={handleClickTreeNode}
                handleKeyboardNavigation={handleKeyboardNavigation}
              />
            ))
          : null}
      </div>
    </BaseTreeItem>
  );
});

const Tree: FunctionComponent<React.PropsWithChildren<TreeProps>> = ({
  tree,
  expanded,
  selected,
  setExpanded,
  setSelected,
  className,
  ...otherProps
}: TreeProps) => {
  const { enableNewLayout } = useAppConfig();
  const {
    classes: {
      label: defaultLabelStyle,
      treeRoot,
      openCloudEndpoint,
      engineClassInheritanceTreeItem,
      engineClassInheritanceListContainer,
      engineClassInheritanceList,
      selectedClassInheritanceListItem,
    },
    cx,
  } = useTreeStyles({ enableNewLayout: enableNewLayout ?? false });
  const { locale } = useLocalization();
  const { sidebarSessionId } = useSideBarContent();

  const handleNodeSelect = useCallback(
    (_event: React.SyntheticEvent, nodeId: string) => setSelected(nodeId),
    [setSelected],
  );

  const handleNodeExpanded = useCallback(
    (_event: React.SyntheticEvent, nodeIds: string[]) => setExpanded(nodeIds),
    [setExpanded],
  );

  const eventParameters = useMemo(() => {
    return {
      locale: locale ?? '',
      refPageUrl: typeof window !== 'undefined' ? window.location.pathname : '',
      sidebarSessionId,
    };
  }, [locale, sidebarSessionId]);

  const handleClickTreeNode = useCallback(
    (node: TreeNode) => (e: React.MouseEvent<HTMLButtonElement>) => {
      setTimeout(() => {
        unifiedLoggerClient.logClickEvent({
          eventName: DocsSiteEventType.TreeNodeClicked,
          parameters: {
            ...eventParameters,
            nodeId: node.id,
            nodePath: node.path ?? '',
          },
        });
      }, 100);
    },
    [eventParameters],
  );

  const handleKeyboardNavigation = useCallback(
    (node: TreeNode) => (e: React.KeyboardEvent<HTMLElement>) => {
      if (isInteractKey(e)) {
        document.getElementById(getTreeItemElementId(node.id))?.click();
      }
    },
    [],
  );

  const renderTree = useCallback(
    (node: TreeNode, depth = 0) => {
      if (node && node.component && node.component === ECustomTreeComponentName.OpenCloudEndpoint) {
        return (
          <OpenCloudEndpointTree
            node={node}
            defaultLabelStyle={defaultLabelStyle}
            openCloudEndpoint={openCloudEndpoint}
            cx={cx}
            handleClickTreeNode={handleClickTreeNode}
            handleKeyboardNavigation={handleKeyboardNavigation}
          />
        );
      }

      if (node && node.path === REFERENCE_ENGINE_CLASSES_NODE_PATH) {
        return (
          <EngineReferenceClassInheritanceTree
            node={node}
            defaultLabelStyle={defaultLabelStyle}
            engineClassInheritanceTreeItem={engineClassInheritanceTreeItem}
            engineClassInheritanceListContainer={engineClassInheritanceListContainer}
            engineClassInheritanceList={engineClassInheritanceList}
            selectedClassInheritanceListItem={selectedClassInheritanceListItem}
            selected={selected}
            cx={cx}
            handleClickTreeNode={handleClickTreeNode}
            handleKeyboardNavigation={handleKeyboardNavigation}
          />
        );
      }

      // Skip links for Inherited from Instance or Inherited from Object in right nav tree
      // For example, ids of format 'properties-inherit-instance', 'methods-inherit-instance'..
      if (node.id && (REGEX_INHERIT_INSTANCE.test(node.id) || REGEX_INHERIT_OBJECT.test(node.id))) {
        return null;
      }

      let nodeClasses = { content: defaultLabelStyle };
      if (node.props?.classes) {
        nodeClasses = {
          ...node.props.classes,
          content: cx(defaultLabelStyle, node.props.classes.label),
        };
      }

      return (
        <BaseTreeItem
          node={node}
          classes={nodeClasses}
          onClick={handleClickTreeNode(node)}
          onKeyDown={handleKeyboardNavigation(node)}>
          {Array.isArray(node.children) ? node.children.map((n) => renderTree(n, depth + 1)) : null}
        </BaseTreeItem>
      );
    },
    [
      defaultLabelStyle,
      handleClickTreeNode,
      handleKeyboardNavigation,
      openCloudEndpoint,
      engineClassInheritanceTreeItem,
      engineClassInheritanceListContainer,
      engineClassInheritanceList,
      selectedClassInheritanceListItem,
      selected,
      cx,
    ],
  );

  const treeElements = useMemo(() => renderTree(tree), [renderTree, tree]);

  return (
    <TreeView
      tabIndex={-1}
      classes={{ root: cx(treeRoot, className) }}
      expanded={expanded}
      selected={selected}
      multiSelect={false}
      onNodeToggle={handleNodeExpanded}
      onNodeSelect={handleNodeSelect}
      defaultExpandIcon={enableNewLayout ? <ExpandMoreIcon /> : undefined}
      defaultCollapseIcon={enableNewLayout ? <ExpandLessIcon /> : undefined}
      {...otherProps}>
      {treeElements}
    </TreeView>
  );
};

export default Tree;
